// src/components/OptimizedCloudCompute.jsx
import React from "react";
import {
  Cpu,
  HardDrive,
  Gauge,
  Rocket,
  Zap,
  Boxes,
  ShieldCheck,
} from "lucide-react";
import FadeInOnScroll from "./FadeInOnScroll"; // 名字随便，但首字母要大写
import FadeInCard from "./FadeInCard";
import SlideInOnScroll from "./SlideInOnScroll";
import { Link } from "react-router-dom";

export default function OptimizedCloudCompute() {
  const benefits = [
    {
      title: "Dedicated vCPU",
      desc: "No noisy neighbors. vCPUs are pinned to physical cores for consistent performance.",
      icon: <Cpu className="w-6 h-6 text-[#7c3aed]" />,
    },
    {
      title: "NVMe SSD throughput",
      desc: "Ultra-fast NVMe storage for low-latency databases, search indices, and message queues.",
      icon: <HardDrive className="w-6 h-6 text-[#7c3aed]" />,
    },
    {
      title: "High clock speed",
      desc: "Perfect for latency-sensitive APIs, real-time analytics and CPU-bound workloads.",
      icon: <Gauge className="w-6 h-6 text-[#7c3aed]" />,
    },
  ];

  const workloads = [
    "High-traffic APIs and backend services",
    "Real-time bidding, trading and telemetry pipelines",
    "Search, indexing and low-latency databases",
    "Continuous integration and large build pipelines",
  ];

  return (
    <main className="relative overflow-hidden bg-gradient-to-b from-purple-50 via-white to-purple-100 text-[#0f0533]">
      {/* 背景柔光 */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-24 -top-10 h-[26rem] w-[26rem] rounded-full bg-purple-300/25 blur-3xl" />
        <div className="absolute right-[-10rem] top-40 h-[30rem] w-[30rem] rounded-full bg-purple-200/30 blur-3xl" />
        <div className="absolute bottom-[-8rem] left-1/3 h-[22rem] w-[22rem] rounded-full bg-fuchsia-200/30 blur-3xl" />
      </div>

      {/* HERO */}
      <section className="relative max-w-6xl mx-auto px-6 pt-28 pb-24 grid gap-12 md:grid-cols-[1.4fr_1fr] items-center">
        <div>
          <SlideInOnScroll from="left">
            <p className="text-sm tracking-[0.3em] font-semibold text-[#7b5cff] mb-3 uppercase">
              Optimized Cloud Compute
            </p>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-4 bg-gradient-to-r from-[#5b21b6] via-[#7c3aed] to-[#a855f7] bg-clip-text text-transparent">
              High-performance VMs
              <br />
              with dedicated resources
            </h1>
            <p className="text-base md:text-lg text-[#3b2c70] max-w-xl">
              Run your most performance-sensitive applications on optimized
              instances with dedicated vCPU, high-clock processors and NVMe SSD
              storage.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                className="rounded-full bg-gradient-to-r from-[#6a2dd9] to-[#9a8dfc] px-6 py-3 text-sm font-semibold text-white shadow-[0_10px_35px_rgba(106,45,217,0.35)] hover:translate-y-[-2px] transition-transform inline-flex items-center justify-center"
              >
                Explore optimized instances
              </Link>
              <p className="text-xs md:text-sm text-[#5b4a99]">
                Ideal for latency-sensitive production workloads.
              </p>
            </div>
          </SlideInOnScroll>
        </div>
        <SlideInOnScroll from="right">
          <div className="hidden md:block rounded-3xl bg-white/80 backdrop-blur-xl border border-purple-200 p-7 shadow-[0_18px_50px_rgba(148,101,255,0.25)]">
            <div className="flex items-center gap-3 mb-3">
              <Rocket className="w-7 h-7 text-[#8b5cf6]" />
              <p className="text-md font-semibold text-[#2a0f4c]">
                Designed for performance
              </p>
            </div>
            <p className="text-md text-[#4b3a82]">
              Optimized Cloud Compute combines dedicated CPU with NVMe storage
              so your mission-critical apps remain fast and responsive, even at
              peak load.
            </p>
          </div>
        </SlideInOnScroll>
      </section>

      {/* BENEFITS */}
      <section className="relative py-16">
        <div className="max-w-6xl mx-auto px-6 grid gap-10 md:grid-cols-2 items-start">
          <SlideInOnScroll from="left">
            {/* 左：Benefits cards */}
            <div className="space-y-5">
              {benefits.map((b) => (
                <div
                  key={b.title}
                  className="rounded-3xl bg-white/85 backdrop-blur-xl border border-purple-200 p-6 shadow-[0_12px_35px_rgba(148,101,255,0.22)] flex gap-4"
                >
                  <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                    {b.icon}
                  </div>
                  <div>
                    <h3 className="text-md font-semibold text-[#2a0f4c] mb-1">
                      {b.title}
                    </h3>
                    <p className="text-sm text-[#4b3a82] leading-relaxed">
                      {b.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </SlideInOnScroll>

          <SlideInOnScroll from="right">
            {/* 右：Workloads list */}
            <div className="rounded-3xl bg-gradient-to-br from-[#f5e9ff] via-white to-[#ecebff] border border-purple-200 p-8 shadow-[0_14px_40px_rgba(148,101,255,0.24)] mt-10">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-7 h-7 text-[#8b5cf6]" />
                <h3 className="text-2xl font-bold text-[#2a0f4c]">
                  Built for demanding workloads
                </h3>
              </div>
              <p className="text-md text-[#3b2c70] mb-4">
                Optimized instances shine when every millisecond matters. Common
                use cases include:
              </p>
              <ul className="text-sm text-[#4b3a82] space-y-2">
                {workloads.map((w) => (
                  <li key={w}>• {w}</li>
                ))}
              </ul>
            </div>
          </SlideInOnScroll>
        </div>
      </section>

      {/* ARCHITECTURE / SCALING */}
      <section className="relative py-20">
        <div className="max-w-6xl mx-auto px-6">
          {/* 大容器卡片，解决背景断层的问题 */}
          <div>
            <div className="grid gap-10 md:grid-cols-2 items-stretch">
              {/* 左卡片 */}
              <FadeInCard delay={0}>
                <div className="h-full rounded-3xl bg-white/80 border border-purple-100 p-8 shadow-[0_10px_30px_rgba(148,101,255,0.18)] flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <Boxes className="w-7 h-7 text-[#7c3aed]" />
                    <h3 className="text-xl font-bold text-[#2a0f4c]">
                      Scale horizontally or vertically
                    </h3>
                  </div>
                  <p className="text-sm text-[#3b2c70] mb-4">
                    Start small and scale up instance size as your workload
                    grows, or scale out across multiple nodes behind a load
                    balancer for resilience and throughput.
                  </p>
                  <ul className="mt-auto text-xs text-[#4b3a82] space-y-2">
                    <li>• Resize instances with minimal downtime.</li>
                    <li>• Create templates for repeatable environments.</li>
                    <li>
                      • Combine with Cloud GPU or Bare Metal for hybrid stacks.
                    </li>
                  </ul>
                </div>
              </FadeInCard>

              {/* 右卡片 */}
              <FadeInCard delay={0.15}>
                <div className="h-full rounded-3xl bg-white/80 border border-purple-100 p-8 shadow-[0_10px_30px_rgba(148,101,255,0.18)] flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center">
                      <ShieldCheck className="w-6 h-6 text-[#7c3aed]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#2a0f4c]">
                      Enterprise-grade reliability
                    </h3>
                  </div>
                  <p className="text-sm text-[#3b2c70] mb-4">
                    Optimized compute nodes run on carefully curated hardware
                    profiles with dedicated monitoring, giving you the
                    confidence to migrate critical production services.
                  </p>
                  <p className="mt-auto text-xs text-[#4b3a82]">
                    Combine with private networking, firewalls and backup
                    policies to build an end-to-end resilient platform.
                  </p>
                </div>
              </FadeInCard>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 bg-gradient-to-r from-[#b892ff]/15 via-white to-[#e0d2ff]/35">
        <FadeInOnScroll>
          <div className="max-w-6xl mx-auto px-6 rounded-3xl bg-white/80 border border-purple-200 shadow-[0_20px_60px_rgba(148,101,255,0.25)] flex flex-col md:flex-row items-center justify-between gap-8 px-8 py-10">
            <div className="space-y-2">
              <p className="text-xs tracking-[0.18em] uppercase text-[#9b6fff]">
                Optimized for performance
              </p>
              <h3 className="text-2xl md:text-2xl font-bold text-[#3d1a6e]">
                Move your latency-sensitive services to Optimized Cloud Compute
              </h3>
              <p className="text-md text-[#4c3670] max-w-xl">
                Get the consistency of dedicated resources without managing
                physical hardware, and keep your customer experience blazing
                fast.
              </p>
            </div>
            <Link
              to="/contact"
              className="rounded-full bg-[#8b5cf6] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#7c3aed] transition"
            >
              View More
            </Link>
          </div>
        </FadeInOnScroll>
      </section>
    </main>
  );
}
